import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../logo.png";
import { signInWithPassword, authWithGoogle, resetPassword } from "../api/auth";
import { initializeUserCredits } from "../api/businesses";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [showResetModal, setShowResetModal] = useState(false); // State to control modal visibility
  const [resetEmail, setResetEmail] = useState(""); // State for the email in the reset modal
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await signInWithPassword(email, password);
      navigate("/furnish-home"); // Redirect to the furnish-home
    } catch (err) {
      setError("Failed to login. Please check your email and password.");
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await authWithGoogle();
      await initializeUserCredits(); // Initialize credits after signup
      navigate("/furnish-home"); // Redirect after successful login
    } catch (err) {
      setError("Failed to sign in with Google.");
    }
  };

  const handleForgotPassword = () => {
    setShowResetModal(true); // Show the modal
    setError("");
    setResetMessage("");
  };

  const handleSendResetEmail = async () => {
    try {
      await resetPassword(resetEmail);
      setResetMessage("Password reset email sent! Check your inbox.");
      setShowResetModal(false); // Hide the modal after success
    } catch (err) {
      setError("Failed to send password reset email. Please try again.");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white shadow-lg rounded-lg">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img src={Logo} alt="Logo" className="w-32 h-32 object-contain" />
        </div>

        <h2 className="text-2xl font-bold text-center text-gray-800">Login</h2>
        {error && <p className="text-red-500 text-center">{error}</p>}
        {resetMessage && (
          <p className="text-green-500 text-center">{resetMessage}</p>
        )}

        <form className="space-y-4" onSubmit={handleLogin}>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-600"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password Field */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-600"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              className="w-full p-2 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-blue-500 hover:underline"
            >
              Forgot Password?
            </button>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            className="w-full py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition duration-200"
          >
            Login
          </button>
        </form>

        {/* Separator */}
        <div className="flex items-center my-4">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="px-3 text-sm text-gray-500">Or continue with</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        <button
          onClick={handleGoogleLogin}
          className="w-full py-2 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition duration-200"
        >
          <img
            src="https://www.svgrepo.com/show/355037/google.svg"
            alt="Google Icon"
            className="w-5 h-5 mr-2"
          />
          <span className="text-gray-600">Login with Google</span>
        </button>

        {/* Link to Signup Page */}
        <p className="text-sm text-center text-gray-600 mt-4">
          Don't have an account?{" "}
          <Link to="/signup" className="text-blue-500 hover:underline">
            Sign up
          </Link>
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
            <h3 className="text-lg font-bold text-center mb-4">
              Reset Password
            </h3>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Enter your email to receive a password reset link.
            </p>
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 mb-4"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSendResetEmail}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
